import { LoadingIcon } from 'morpheus-ui';
import React, { ComponentProps, HTMLAttributeAnchorTarget } from 'react';
import { Link } from 'react-router-dom';
import { VariantProps } from 'tailwind-variants';
import { Icon } from '../Icon';
import { blockIfReadOnly } from '../../../accessControl/guard';
import {
  button,
  buttonIcon,
  buttonMaterialIcon,
  loadingIcon,
} from './variants';

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof button> & {
    LeftIcon?: React.ComponentType<ComponentProps<'svg'>> | string;
    iconType?: 'svg' | 'material';
    iconSize?: 'mini' | 'small' | 'medium' | 'regular' | 'large' | 'clearSmall';
    classNameIcon?: string;
    iconVariant?: 'rounded' | 'fill';
    asLink?: boolean;
    to?: string;
    target?: HTMLAttributeAnchorTarget;
    iconId?: string;
    /**
     * Libera o clique no modo somente-leitura do freemium. Use em botões de
     * navegação/visualização (menu, voltar, filtrar, paginar, trocar aba,
     * fechar modal) e no próprio fluxo de upsell/checkout. Sem isso, no modo
     * somente-leitura o clique abre o modal de upsell em vez de executar.
     */
    allowReadOnly?: boolean;
  };

function extractText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join(' ');
  }
  if (React.isValidElement(node)) {
    return extractText((node.props as { children?: React.ReactNode }).children);
  }
  return '';
}

function Button({
  size = 'regular',
  variant = 'contained',
  orientation = 'horizontal',
  children,
  className,
  classNameIcon,
  iconVariant,
  loading,
  negative,
  disabled,
  LeftIcon,
  iconType = 'material',
  iconSize = 'regular',
  onlyIcon,
  full,
  asLink = false,
  to,
  target = '_self',
  iconId,
  allowReadOnly = false,
  onClick,
  ...props
}: ButtonProps) {
  const isEmpty = !children;

  // Botões com texto "como funciona" são informativos e ficam sempre
  // liberados no modo somente-leitura do freemium.
  const isHowItWorks = /como funciona/i.test(extractText(children));

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!allowReadOnly && !isHowItWorks && blockIfReadOnly()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };

  const buttonElement = (
    <button
      {...props}
      onClick={handleClick}
      disabled={disabled || loading}
      className={button({
        variant,
        size,
        orientation,
        negative,
        disabled,
        className,
        full,
        onlyIcon: onlyIcon ?? isEmpty,
      })}
    >
      {LeftIcon && !loading && (
        <Icon
          id={iconId ?? `system-button-icon-${LeftIcon}`}
          Icon={LeftIcon}
          variant={iconVariant}
          type={iconType}
          className={
            iconType === 'svg'
              ? buttonIcon({ iconSize, variant, className: classNameIcon })
              : buttonMaterialIcon({
                  variant,
                  iconSize,
                  className: classNameIcon,
                })
          }
        />
      )}
      {loading ? (
        <LoadingIcon className={loadingIcon({ size, variant })} />
      ) : null}
      {!onlyIcon && children}
    </button>
  );

  if (asLink) {
    return (
      <Link to={to ?? ''} target={target} className={full ? 'w-full' : ''}>
        {buttonElement}
      </Link>
    );
  }

  return buttonElement;
}

export { Button };
